import {
  GetMarketplaceSettingsDocument,
  GetMarketplaceSettingsQueryVariables,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'

export type QuestionType = 'text' | 'selection' | 'toggler'

interface QuestionBase {
  type: QuestionType
  label: string
  order: number
  id: string
  required: boolean
}

export type InputType = 'text' | 'password' | 'time' | 'number' | 'email'

export interface TextField extends QuestionBase {
  type: QuestionType
  placeholder?: string | null
  description?: string | null
  defaultValue?: string | null
  inputType: InputType
}

export interface SelectionField extends QuestionBase {
  type: QuestionType
  description?: string | null
  placeholder?: string | null
  multipleSelection: boolean
  searchable: boolean
  searchPlaceholder?: string | null
  defaultValues?: string | null
  options: {
    label: string
    value: string
  }[]
}

export interface TogglerField extends QuestionBase {
  type: QuestionType
  disabled: boolean
  description?: string | null
  defaultValue?: boolean | null
  information?: string | null
}

export type Field = TextField | SelectionField | TogglerField

export interface Settings {
  integrationId: string
  title: string
  description?: string | null
  questions: Field[]
  hasEncryptedFields: boolean
}

export const useGetMarketplaceSettings = (
  variables: GetMarketplaceSettingsQueryVariables,
  skip = false
) => {
  const { loading, data } = useQuery(GetMarketplaceSettingsDocument, { variables, skip })

  const settings = data?.marketplace.settingsConnection.nodes[0]
  if (settings) {
    const textQuestionsWithType: TextField[] = settings?.questions.textFieldQuestions.map(
      (question) => ({
        label: question.label,
        order: question.order,
        id: question.id,
        required: question.required,
        placeholder: question.placeholder,
        description: question.description,
        defaultValue: question.defaultValue,
        inputType: question.inputType || 'text',
        type: 'text',
      })
    )

    const selectionQuestionsWithType: SelectionField[] = settings?.questions.selectionQuestions.map(
      (question) => ({
        id: question.id,
        description: question.description,
        label: question.label,
        multipleSelection: question.multipleSelection,
        placeholder: question.placeholder,
        defaultValues: question.defaultValues && question.defaultValues[0],
        searchPlaceholder: question.searchBarPlaceholder,
        searchable: question.searchable,
        order: question.order,
        required: question.required,
        options: question.options,
        type: 'selection',
      })
    )

    const togglerQuestionsWithType: TogglerField[] = settings?.questions.toggleQuestions.map(
      (question) => ({
        id: question.id,
        label: question.label,
        defaultValue: question.defaultValue,
        information: question.description,
        order: question.order,
        required: question.required,
        disabled: question.disabled,
        tooltipText: question.tooltipText,
        type: 'toggler',
      })
    )

    const combinedQuestions = [
      ...textQuestionsWithType,
      ...selectionQuestionsWithType,
      ...togglerQuestionsWithType,
    ]

    const orderedQuestions = combinedQuestions.sort((a, b) => a.order - b.order)

    const settingsData: Settings = {
      integrationId: settings.integrationId,
      title: settings.title,
      description: settings.description,
      questions: orderedQuestions,
      hasEncryptedFields: settings.hasEncryptedFields,
    }

    return {
      loading,
      data: settingsData,
    }
  }

  return {
    loading,
    data: undefined,
  }
}
